import { forwardRef, useCallback } from 'react';
import styles from './Image.module.scss.js';
import messages from './intl/index.js';
import { useImageLoadingState } from './useImageLoadingState.js';
import { asElementProps } from './util.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMessageFormatter } from '../i18n/format.js';
import { StandaloneIcon } from '../icon/Icon.js';
import { MdOutlineBrokenImage } from '../node_modules/react-icons/md/index.js';
import { Text } from '../text/Text.js';
import classNames from 'clsx';

/**
 * An image to display.
 */
const Image = /*#__PURE__*/forwardRef(function Image(props, ref) {
  const {
    src,
    alt,
    objectFit = 'contain',
    className: callerClassName,
    tag,
    sources = [],
    onStateChange,
    cornerRadius = 'default',
    background = 'transparent',
    height,
    width,
    ...rest
  } = props;
  const {
    formatMessage
  } = useMessageFormatter(messages);
  const handleStateChange = useCallback(state => {
    onStateChange?.(state);
  }, [onStateChange]);
  const [state, imgProps] = useImageLoadingState({
    src,
    onStateChange: handleStateChange
  });
  const sourceEls = sources.map((x, i) => /*#__PURE__*/jsx("source", {
    ...asElementProps(x)
  }, i));
  const className = classNames('dsx-component', styles['image'], styles[`radius-${cornerRadius}`], styles[`background-${background}`], state === 'failure' ? styles['failure'] : styles[objectFit], callerClassName);
  const pictureEl = /*#__PURE__*/jsxs("picture", {
    children: [sourceEls, /*#__PURE__*/jsx("img", {
      ref: ref,
      alt: alt,
      className: className,
      height: height,
      width: width,
      ...imgProps,
      ...rest
    })]
  });
  switch (state) {
    case 'initial':
    case 'loading':
    case 'success':
      return pictureEl;
    case 'failure':
      return /*#__PURE__*/jsxs("div", {
        className: className,
        "data-dsx--image": tag,
        style: {
          height: height && `${height}px`,
          width: width && `${width}px`
        },
        children: [/*#__PURE__*/jsx(StandaloneIcon, {
          icon: MdOutlineBrokenImage,
          size: "24px",
          title: "",
          color: "color-neutral-300"
        }), /*#__PURE__*/jsx(Text, {
          variant: "body-xsmall",
          color: "color-neutral-700",
          children: cornerRadius != 'small' && formatMessage('image-not-available')
        })]
      });
  }
});

export { Image };
